import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} AxCollectionSeoContentRefs
 * @property {HTMLElement} content - Contenu de la page liée (rendu en entier côté serveur)
 * @property {HTMLButtonElement} toggle - Bouton "Lire la suite" / "Lire moins"
 * @property {HTMLElement} labelMore - Libellé affiché replié
 * @property {HTMLElement} labelLess - Libellé affiché déplié
 */

/**
 * Bloc de contenu SEO en bas de PLP (section `ax-collection-seo-content`).
 *
 * Le texte est entier dans le DOM et replié par CSS (`data-collapsed="true"` sur l'hôte).
 * Ce composant :
 *   1. retire le fondu et le bouton quand le texte tient dans la hauteur repliée (`data-fits`) ;
 *   2. ouvre et referme le texte, avec une transition de hauteur ;
 *   3. déplie le texte quand le focus clavier entre dans sa partie masquée.
 *
 * @extends {Component<AxCollectionSeoContentRefs>}
 */
class AxCollectionSeoContent extends Component {
	requiredRefs = ['content', 'toggle', 'labelMore', 'labelLess'];

	/** @type {ResizeObserver | null} */
	#resizeObserver = null;

	/** @type {((event: TransitionEvent) => void) | null} */
	#onTransitionEnd = null;

	connectedCallback() {
		super.connectedCallback();

		this.#syncCollapsible();
		this.#resizeObserver = new ResizeObserver(() => this.#syncCollapsible());
		this.#resizeObserver.observe(this.refs.content);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#resizeObserver?.disconnect();
		this.#resizeObserver = null;
		if (this.refs.content) this.#clearAnimation(this.refs.content);
	}

	/**
	 * Le repli n'a de sens que si le texte déborde de la hauteur repliée.
	 * `data-fits` retire alors le bouton et le fondu (CSS). La hauteur maximale,
	 * elle, reste : c'est elle qui permet de voir le texte déborder à nouveau
	 * après un redimensionnement.
	 * Un texte déplié par l'utilisateur garde son bouton (pour le refermer).
	 *
	 * @private
	 */
	#syncCollapsible() {
		if (this.dataset.collapsed !== 'true') return;

		const { content, toggle } = this.refs;
		const fits = content.scrollHeight <= content.clientHeight + 1;
		toggle.hidden = fits;
		this.toggleAttribute('data-fits', fits);
	}

	/**
	 * Ouvre ou referme le texte (via `on:click` sur le bouton).
	 */
	toggle() {
		this.#setExpanded(this.dataset.collapsed === 'true');
	}

	/**
	 * Déplie le texte quand le focus clavier y entre : sans ça, la tabulation
	 * passerait par des liens masqués sous le fondu.
	 *
	 * @param {FocusEvent} event - Événement focus délégué par le framework
	 */
	expandOnFocus(event) {
		if (this.dataset.collapsed !== 'true' || this.hasAttribute('data-fits')) return;

		const focused = event.composedPath?.()[0];
		if (!(focused instanceof Element) || !focused.matches(':focus-visible')) return;

		this.#setExpanded(true);
	}

	/**
	 * @private
	 * @param {boolean} expanded - true = déplier
	 */
	#setExpanded(expanded) {
		const { content, toggle, labelMore, labelLess } = this.refs;

		this.#animateHeight(content, expanded);

		this.dataset.collapsed = expanded ? 'false' : 'true';
		toggle.setAttribute('aria-expanded', String(expanded));
		labelMore.hidden = expanded;
		labelLess.hidden = !expanded;

		if (!expanded) this.#keepInView();
	}

	/**
	 * Après fermeture d'un long texte, le haut du bloc peut se retrouver au-dessus
	 * de l'écran : on y ramène la vue pour ne pas perdre le lecteur.
	 *
	 * @private
	 */
	#keepInView() {
		// `scroll-margin-block-start` (CSS) réserve la hauteur du header sticky.
		const offset = parseFloat(getComputedStyle(this).scrollMarginBlockStart) || 0;
		if (this.getBoundingClientRect().top >= offset) return;

		this.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
	}

	/**
	 * Anime la hauteur entre l'état replié et déplié. Une hauteur `auto` ne
	 * s'interpole pas : on mesure les deux hauteurs réelles, on fige celle de
	 * départ puis on laisse la transition CSS rejoindre celle d'arrivée.
	 *
	 * @private
	 * @param {HTMLElement} content
	 * @param {boolean} opening - true = on déplie
	 */
	#animateHeight(content, opening) {
		// Hauteur affichée, y compris au milieu d'un trajet en cours : un second
		// clic repart de là où le bloc se trouve.
		const from = content.getBoundingClientRect().height;

		// Les styles en ligne d'un trajet en cours fausseraient la mesure d'arrivée.
		this.#clearAnimation(content);

		if (prefersReducedMotion()) return;

		// Hauteur d'arrivée, mesurée dans l'état cible sans la peindre.
		const previous = this.dataset.collapsed;
		this.dataset.collapsed = opening ? 'false' : 'true';
		const to = content.getBoundingClientRect().height;
		this.dataset.collapsed = previous;

		// Aucune transition ne partirait, donc aucun `transitionend` pour nettoyer.
		if (Math.abs(to - from) < 1) return;

		// `max-height: none` en ligne le temps du trajet : à la fermeture, la hauteur
		// maximale de l'état replié couperait sinon le texte d'un coup.
		content.style.height = `${from}px`;
		content.style.maxHeight = 'none';
		content.style.overflow = 'hidden';

		// Force le calcul du style de départ, sinon rien ne s'anime.
		void content.offsetHeight;

		content.style.transition = 'height var(--animation-speed-slow) var(--ease-out-cubic)';
		content.style.height = `${to}px`;

		/** @param {TransitionEvent} event */
		this.#onTransitionEnd = (event) => {
			// Les transitions des liens du texte remontent aussi jusqu'ici.
			if (event.target !== content || event.propertyName !== 'height') return;

			this.#clearAnimation(content);
		};

		content.addEventListener('transitionend', this.#onTransitionEnd);
		content.addEventListener('transitioncancel', this.#onTransitionEnd);
	}

	/**
	 * Retire les styles en ligne et l'écouteur d'un trajet de hauteur.
	 *
	 * @private
	 * @param {HTMLElement} content
	 */
	#clearAnimation(content) {
		if (this.#onTransitionEnd) {
			content.removeEventListener('transitionend', this.#onTransitionEnd);
			content.removeEventListener('transitioncancel', this.#onTransitionEnd);
			this.#onTransitionEnd = null;
		}

		for (const property of ['height', 'max-height', 'overflow', 'transition']) {
			content.style.removeProperty(property);
		}
	}
}

if (!customElements.get('ax-collection-seo-content')) {
	customElements.define('ax-collection-seo-content', AxCollectionSeoContent);
}
